import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';
import { METRO_PHASE_INDEX } from '@/config/metro-conversation-states';

export const searchCoursesToolMetroSchema: ToolSchema = {
  type: 'function',
  name: 'search_courses',
  description:
    'Search the Metro course catalog to find relevant courses based on a query and constraints. ' +
    'Use this to fetch course options for the employee.',
  parameters: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query describing the course needs or skill gaps'
      },
      limit: {
        type: 'number',
        description: 'Maximum number of course results to return'
      },
      goalIndex: {
        type: 'number',
        description:
          'The 0-based index of the goal card this search belongs to. ' +
          'Results will appear above that goal card on screen. ' +
          'Required when goal cards are displayed.'
      },
      goalTitle: {
        type: 'string',
        description:
          'Short German label for this goal (max 6 words). ' +
          'Used to auto-create the goal card if it does not exist yet — ' +
          'always provide this alongside goalIndex.'
      }
    },
    required: ['query'],
    additionalProperties: false
  }
};

export const searchCoursesToolMetroImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  try {
    const { query, limit, goalIndex, goalTitle } = args ?? {};
    if (!query || typeof query !== 'string') {
      return { success: false, error: 'Invalid query parameter.' };
    }

    const baseUrl = ''; // Azure Functions are same-origin under the web app API
    const response = await fetch(`${baseUrl}/api/metro/course-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, limit })
    });

    if (!response.ok) {
      const text = await response.text();
      return { success: false, error: text || 'Course search API error.' };
    }

    const json = await response.json();
    const results = json?.results ?? [];

    if (typeof window !== 'undefined') {
      const phaseIndex = METRO_PHASE_INDEX['course_search_results'];
      window.dispatchEvent(
        new CustomEvent('metro-phase-changed', {
          detail: { stateId: 'course_search_results', phaseIndex, label: 'course_search_results' }
        })
      );

      if (Array.isArray(results) && results.length > 0) {
        window.dispatchEvent(
          new CustomEvent('metro-show-course-cards', {
            detail: {
              cards: results,
              durationMs: 30000,
              goalIndex: typeof goalIndex === 'number' ? goalIndex : null,
              goalTitle: typeof goalTitle === 'string' ? goalTitle.trim() : null
            }
          })
        );
      }
    }

    return { success: true, data: results };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error in course search tool'
    };
  }
};
